import { useEffect, useMemo, useState } from 'react';
import {
  createPaperGrade,
  createPaperWidth,
  deletePaperGrade,
  deletePaperWidth,
  fetchPaperGrades,
  fetchPaperWidths,
  updatePaperGrade,
  updatePaperWidth,
} from '../services/api';
import { useCan } from '../hooks/useCan';
import './CustomersPage.css';
import './GradesPage.css';

const emptyGrade = { code: '', name: '', grams: '', active: true, note: '' };
const emptyWidth = { mm: '', active: true, note: '' };

function gramsToText(grams) {
  return Array.isArray(grams) ? grams.join(', ') : '';
}

export default function GradesPage() {
  const canManage = useCan('masters.manage');
  const [tab, setTab] = useState('grades');
  const [grades, setGrades] = useState([]);
  const [widths, setWidths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [gradeForm, setGradeForm] = useState(emptyGrade);
  const [widthForm, setWidthForm] = useState(emptyWidth);
  const [editingGradeId, setEditingGradeId] = useState('');
  const [editingWidthId, setEditingWidthId] = useState('');
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [g, w] = await Promise.all([fetchPaperGrades(false), fetchPaperWidths(false)]);
      setGrades(g || []);
      setWidths(w || []);
    } catch (err) {
      setError(err.message || 'โหลด master ไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filteredGrades = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return grades;
    return grades.filter((row) => {
      const hay = `${row.code || ''} ${row.name || ''} ${gramsToText(row.grams)} ${row.note || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [grades, query]);

  const filteredWidths = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return widths;
    return widths.filter((row) => {
      const hay = `${row.mm || ''} ${row.note || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [widths, query]);

  function startEditGrade(row) {
    setEditingGradeId(row.id);
    setGradeForm({
      code: row.code || '',
      name: row.name || '',
      grams: gramsToText(row.grams),
      active: row.active !== false,
      note: row.note || '',
    });
  }

  function startEditWidth(row) {
    setEditingWidthId(row.id);
    setWidthForm({
      mm: String(row.mm || ''),
      active: row.active !== false,
      note: row.note || '',
    });
  }

  function resetGradeForm() {
    setEditingGradeId('');
    setGradeForm(emptyGrade);
  }

  function resetWidthForm() {
    setEditingWidthId('');
    setWidthForm(emptyWidth);
  }

  async function saveGrade(e) {
    e.preventDefault();
    if (!gradeForm.code.trim()) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        code: gradeForm.code.trim().toUpperCase(),
        name: (gradeForm.name || gradeForm.code).trim().toUpperCase(),
        grams: gradeForm.grams,
        active: Boolean(gradeForm.active),
        note: gradeForm.note,
      };
      if (editingGradeId) {
        await updatePaperGrade(editingGradeId, payload);
      } else {
        await createPaperGrade(payload);
      }
      resetGradeForm();
      await load();
    } catch (err) {
      setError(err.message || 'บันทึกเกรดไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  }

  async function saveWidth(e) {
    e.preventDefault();
    if (!String(widthForm.mm).trim()) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        mm: Number(widthForm.mm),
        active: Boolean(widthForm.active),
        note: widthForm.note,
      };
      if (editingWidthId) {
        await updatePaperWidth(editingWidthId, payload);
      } else {
        await createPaperWidth(payload);
      }
      resetWidthForm();
      await load();
    } catch (err) {
      setError(err.message || 'บันทึกหน้ากว้างไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteGrade(row) {
    if (!window.confirm(`ลบเกรด "${row.code}" ?`)) return;
    try {
      await deletePaperGrade(row.id);
      if (editingGradeId === row.id) resetGradeForm();
      await load();
    } catch (err) {
      setError(err.message || 'ลบเกรดไม่สำเร็จ');
    }
  }

  async function handleDeleteWidth(row) {
    if (!window.confirm(`ลบหน้ากว้าง ${row.mm} mm ?`)) return;
    try {
      await deletePaperWidth(row.id);
      if (editingWidthId === row.id) resetWidthForm();
      await load();
    } catch (err) {
      setError(err.message || 'ลบหน้ากว้างไม่สำเร็จ');
    }
  }

  async function toggleGradeActive(row) {
    try {
      await updatePaperGrade(row.id, { active: !row.active });
      await load();
    } catch (err) {
      setError(err.message || 'อัปเดตสถานะไม่สำเร็จ');
    }
  }

  async function toggleWidthActive(row) {
    try {
      await updatePaperWidth(row.id, { active: !row.active });
      await load();
    } catch (err) {
      setError(err.message || 'อัปเดตสถานะไม่สำเร็จ');
    }
  }

  return (
    <div className="customers-page grades-page stack">
      <header className="data-hero">
        <div>
          <p className="data-kicker">Master Data</p>
          <h1>เกรดกระดาษ</h1>
          <p className="muted data-sub">
            เกรด/แกรม และหน้ากว้างแยกกัน — เพิ่ม/แก้ได้โดยไม่ต้องแก้โค้ด
          </p>
        </div>
      </header>

      <div className="grades-tabs" role="tablist" aria-label="ชนิด master">
        <button
          type="button"
          role="tab"
          className={`grades-tab${tab === 'grades' ? ' active' : ''}`}
          aria-selected={tab === 'grades'}
          onClick={() => setTab('grades')}
        >
          เกรด + แกรม ({grades.length})
        </button>
        <button
          type="button"
          role="tab"
          className={`grades-tab${tab === 'widths' ? ' active' : ''}`}
          aria-selected={tab === 'widths'}
          onClick={() => setTab('widths')}
        >
          หน้ากว้าง ({widths.length})
        </button>
      </div>

      <section className="card customers-search">
        <input
          className="search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={tab === 'grades' ? 'ค้นหาเกรด / แกรม...' : 'ค้นหาหน้ากว้าง...'}
          enterKeyHint="search"
        />
      </section>

      {error ? <p style={{ color: 'var(--ng)', margin: 0 }}>{error}</p> : null}
      {loading ? <p className="muted">กำลังโหลด...</p> : null}

      {tab === 'grades' ? (
        <>
          {canManage ? (
          <form className="card stack grades-form" onSubmit={saveGrade}>
            <strong>{editingGradeId ? 'แก้ไขเกรด' : 'เพิ่มเกรด'}</strong>
            <div className="grades-form-grid">
              <div className="field">
                <label htmlFor="grade-code">รหัสเกรด *</label>
                <input
                  id="grade-code"
                  value={gradeForm.code}
                  onChange={(e) =>
                    setGradeForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))
                  }
                  placeholder="เช่น KA"
                  required
                  autoComplete="off"
                />
              </div>
              <div className="field">
                <label htmlFor="grade-name">ชื่อแสดง</label>
                <input
                  id="grade-name"
                  value={gradeForm.name}
                  onChange={(e) => setGradeForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="ว่าง = ใช้รหัส"
                  autoComplete="off"
                />
              </div>
              <div className="field field-span-2">
                <label htmlFor="grade-grams">แกรม (คั่นด้วยจุลภาค)</label>
                <input
                  id="grade-grams"
                  value={gradeForm.grams}
                  onChange={(e) => setGradeForm((p) => ({ ...p, grams: e.target.value }))}
                  placeholder="เช่น 125, 150, 185"
                  autoComplete="off"
                />
              </div>
              <div className="field field-span-2">
                <label htmlFor="grade-note">หมายเหตุ</label>
                <input
                  id="grade-note"
                  value={gradeForm.note}
                  onChange={(e) => setGradeForm((p) => ({ ...p, note: e.target.value }))}
                  autoComplete="off"
                />
              </div>
              <label className="grades-check field-span-2">
                <input
                  type="checkbox"
                  checked={Boolean(gradeForm.active)}
                  onChange={(e) =>
                    setGradeForm((p) => ({ ...p, active: e.target.checked }))
                  }
                />
                เปิดใช้ใน dropdown (เกรดที่ใช้)
              </label>
            </div>
            <div className="grades-form-actions">
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? 'กำลังบันทึก...' : editingGradeId ? 'บันทึก' : 'เพิ่มเกรด'}
              </button>
              {editingGradeId ? (
                <button className="btn" type="button" onClick={resetGradeForm}>
                  ยกเลิก
                </button>
              ) : null}
            </div>
          </form>
          ) : null}

          {!loading && filteredGrades.length === 0 ? (
            <section className="card customers-empty">
              <p className="muted" style={{ margin: 0 }}>
                {query.trim() ? 'ไม่พบเกรดที่ค้นหา' : 'ยังไม่มีเกรดใน master'}
              </p>
            </section>
          ) : null}

          {filteredGrades.length > 0 ? (
            <section className="card customers-list-card">
              <div className="customers-list">
                {filteredGrades.map((row) => (
                  <article key={row.id} className="customer-row">
                    <div className="customer-row-body">
                      <strong>
                        {row.code}
                        {!row.active ? (
                          <span className="grades-inactive"> · ปิดใช้</span>
                        ) : null}
                      </strong>
                      <span className="muted">
                        แกรม {gramsToText(row.grams) || '—'}
                      </span>
                      {row.note ? <span className="muted customer-note">{row.note}</span> : null}
                    </div>
                    {canManage ? (
                    <div className="customer-row-actions">
                      <button className="btn" type="button" onClick={() => startEditGrade(row)}>
                        แก้ไข
                      </button>
                      <button
                        className="btn"
                        type="button"
                        onClick={() => toggleGradeActive(row)}
                      >
                        {row.active ? 'ปิดใช้' : 'เปิดใช้'}
                      </button>
                      <button
                        className="btn btn-danger"
                        type="button"
                        onClick={() => handleDeleteGrade(row)}
                      >
                        ลบ
                      </button>
                    </div>
                    ) : null}
                  </article>
                ))}
              </div>
            </section>
          ) : null}
        </>
      ) : (
        <>
          {canManage ? (
          <form className="card stack grades-form" onSubmit={saveWidth}>
            <strong>{editingWidthId ? 'แก้ไขหน้ากว้าง' : 'เพิ่มหน้ากว้าง'}</strong>
            <p className="muted" style={{ margin: 0, fontSize: '0.88rem' }}>
              Master นี้ใช้ร่วมกันทุกเกรด — ไม่ผูกกับ AA–CM
            </p>
            <div className="grades-form-grid">
              <div className="field">
                <label htmlFor="width-mm">หน้ากว้าง (mm) *</label>
                <input
                  id="width-mm"
                  value={widthForm.mm}
                  onChange={(e) =>
                    setWidthForm((p) => ({
                      ...p,
                      mm: e.target.value.replace(/\D/g, ''),
                    }))
                  }
                  placeholder="เช่น 980"
                  inputMode="numeric"
                  required
                  autoComplete="off"
                />
              </div>
              <div className="field">
                <label htmlFor="width-note">หมายเหตุ</label>
                <input
                  id="width-note"
                  value={widthForm.note}
                  onChange={(e) => setWidthForm((p) => ({ ...p, note: e.target.value }))}
                  autoComplete="off"
                />
              </div>
              <label className="grades-check field-span-2">
                <input
                  type="checkbox"
                  checked={Boolean(widthForm.active)}
                  onChange={(e) =>
                    setWidthForm((p) => ({ ...p, active: e.target.checked }))
                  }
                />
                เปิดใช้ใน dropdown
              </label>
            </div>
            <div className="grades-form-actions">
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? 'กำลังบันทึก...' : editingWidthId ? 'บันทึก' : 'เพิ่มหน้ากว้าง'}
              </button>
              {editingWidthId ? (
                <button className="btn" type="button" onClick={resetWidthForm}>
                  ยกเลิก
                </button>
              ) : null}
            </div>
          </form>
          ) : null}

          {!loading && filteredWidths.length === 0 ? (
            <section className="card customers-empty">
              <p className="muted" style={{ margin: 0 }}>
                {query.trim() ? 'ไม่พบหน้ากว้างที่ค้นหา' : 'ยังไม่มีหน้ากว้างใน master'}
              </p>
            </section>
          ) : null}

          {filteredWidths.length > 0 ? (
            <section className="card customers-list-card">
              <div className="customers-list">
                {filteredWidths.map((row) => (
                  <article key={row.id} className="customer-row">
                    <div className="customer-row-body">
                      <strong>
                        {row.mm} mm
                        {!row.active ? (
                          <span className="grades-inactive"> · ปิดใช้</span>
                        ) : null}
                      </strong>
                      {row.note ? <span className="muted customer-note">{row.note}</span> : null}
                    </div>
                    {canManage ? (
                    <div className="customer-row-actions">
                      <button className="btn" type="button" onClick={() => startEditWidth(row)}>
                        แก้ไข
                      </button>
                      <button
                        className="btn"
                        type="button"
                        onClick={() => toggleWidthActive(row)}
                      >
                        {row.active ? 'ปิดใช้' : 'เปิดใช้'}
                      </button>
                      <button
                        className="btn btn-danger"
                        type="button"
                        onClick={() => handleDeleteWidth(row)}
                      >
                        ลบ
                      </button>
                    </div>
                    ) : null}
                  </article>
                ))}
              </div>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}
